import type { ReactNode } from 'react'

import { useAuth } from '@/auth/use-auth'
import type { PermissionCode } from '@/lib/permissions'

export interface PermissionGateProps {
  /** Render the children only when the user holds this permission. */
  permission?: PermissionCode
  /** Render when the user holds at least one of these. Combined with `permission` as an AND. */
  anyOf?: readonly PermissionCode[]
  /** What to render instead. Usually nothing — a disabled control invites a support call. */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Conditional rendering by permission code.
 *
 * This is presentation, not enforcement. The server authorises every command in its own pipeline
 * against the live permission catalogue, and an operator who types a URL, replays a request, or
 * edits the session's permission list in memory gets a 403 all the same. Hiding a control here
 * only spares people buttons they cannot use — never treat a gate as the reason an action is safe.
 */
export function PermissionGate({ permission, anyOf, fallback = null, children }: PermissionGateProps) {
  const { hasPermission, hasAnyPermission } = useAuth()

  const allowed =
    (permission === undefined || hasPermission(permission)) &&
    (anyOf === undefined || hasAnyPermission(anyOf))

  return allowed ? <>{children}</> : <>{fallback}</>
}
